// ─────────────────────────────────────────────────────────────
// PublishScriptDialog
//
// Opened when the user toggles a private script's "Share with community"
// button. Lets the author confirm the paired sample image (curated via
// ScriptMetadataEditDialog and pre-filled from `default_image_id`) before
// publishing — one click to confirm, Change to swap, Remove to publish
// without an image.
//
// Behavior (Greg, 2026-05-15 — replaced the segmented My Images/Shared
// picker + search + scrolling grid with a compact PairedImageRow that
// mirrors the SaveScriptModal UX):
//   • PairedImageRow shows the currently-paired image (thumbnail + name)
//     with Change/Remove buttons, or a dashed "No image paired / Pick"
//     placeholder when nothing is selected.
//   • Click Change → LibrarySelectModal opens on top of this dialog. On
//     pick, the row updates and the dialog body stays put — no re-mount,
//     no scroll reset.
//   • Click Remove → clears the pairing; publish goes through with no
//     image (backend leaves `default_image_id` unchanged per existing
//     behavior).
//   • If the selected image is a private My Images image, an auto-share
//     info Alert appears below the row — publish flips it to global on
//     the backend, same as before.
//   • Unpublish (public → private) still does NOT use this dialog — it
//     stays a one-click action on the My Scripts card.
//   • Cancel = no API call. Publish = POST /api/scripts/:id/publish with
//     { is_public: true, image_id: <selected or null> }.
//
// On success the caller refreshes its scripts list via `onSuccess`.
// ─────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { imageApi, userScriptsApi } from "../../api/client";
import type {
  LibraryImage,
  UserImage,
  UserScript,
} from "../../api/client";
import type { PlaceholderImage } from "./types";
import { LibrarySelectModal, PairedImageRow } from "./modals";

// ── Types ─────────────────────────────────────────────────────

type ImageSegment = "mine" | "shared";

interface PublishScriptDialogProps {
  open: boolean;
  /** The script being published. Null when the dialog is closed. */
  scriptId: number | null;
  /** Optional title — shown in the dialog header for context. */
  scriptTitle?: string | null;
  /**
   * Optional pre-selected image id — when the script already has a paired
   * image (`default_image_id` from `ScriptMetadataEditDialog` curation), pass
   * it here so the dialog opens with that image surfaced in the PairedImageRow
   * (resolved against My Images first, then Shared). Falls back gracefully to
   * the "No image paired" placeholder when the image isn't in either list
   * (e.g., deleted or visibility changed).
   */
  defaultImageId?: number | null;
  onClose: () => void;
  /** Fired after a successful publish with the updated script payload. */
  onSuccess: (updated: UserScript) => void;
}

// ── Helpers ───────────────────────────────────────────────────

/**
 * Shape a `UserImage` or `LibraryImage` (raw API record) into the
 * `PlaceholderImage` shape that `PairedImageRow` expects. Mirrors the
 * conversion `LibrarySelectModal.handleSelect` does — kept inline here so
 * the dialog can synthesize the row's input without re-running the picker.
 */
function toPlaceholderImage(
  img: UserImage | LibraryImage,
  source: "user" | "library",
): PlaceholderImage {
  return {
    id: String(img.id),
    name: img.name,
    description: img.description ?? "",
    tags: img.tags ?? null,
    type: img.image_type as PlaceholderImage["type"],
    source,
    acquisition_metadata: img.acquisition_metadata ?? null,
    metadata_source: img.metadata_source ?? null,
    derived_tags: img.derived_tags ?? null,
  };
}

// ── Main component ────────────────────────────────────────────

export function PublishScriptDialog({
  open,
  scriptId,
  scriptTitle,
  defaultImageId = null,
  onClose,
  onSuccess,
}: PublishScriptDialogProps) {
  const [myImages, setMyImages] = useState<UserImage[]>([]);
  const [sharedImages, setSharedImages] = useState<LibraryImage[]>([]);
  const [imagesError, setImagesError] = useState<string | null>(null);

  // Track which segment ("mine" vs "shared") the current selection lives in.
  // Needed for the auto-share warning logic (only fires when the selection
  // is a private My Images image that's about to flip to global). Set in
  // pair with `selectedId` — either both are null or both are populated.
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedSegment, setSelectedSegment] = useState<ImageSegment | null>(null);

  // Picker modal — opens on top of this dialog when the user clicks Change
  // (or Pick on the empty-state placeholder). MUI z-index stacking lands
  // the picker above; this dialog stays put underneath. Mirrors the
  // SaveScriptModal pattern used in PythonTab.
  const [pickerOpen, setPickerOpen] = useState(false);

  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);

  // Reset selection + errors every time the dialog opens. The fetch effect
  // below repopulates the lists and applies the `defaultImageId` pre-select
  // once the lists resolve.
  useEffect(() => {
    if (!open) return;
    setSelectedId(null);
    setSelectedSegment(null);
    setPublishError(null);
  }, [open]);

  // Fetch both image lists on open, then apply pre-selection (if any) once
  // both lists are known so we can pick the right segment. We still need
  // both lists even though the picker is now a separate modal — we use
  // them to (a) resolve `defaultImageId` into a row and (b) drive the
  // auto-share warning (depends on the picked image's `is_global` flag).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setImagesError(null);
    Promise.all([imageApi.getMyImages(), imageApi.getSharedImages()])
      .then(([mine, shared]) => {
        if (cancelled) return;
        setMyImages(mine);
        setSharedImages(shared);

        // Apply pre-selection from `defaultImageId`. Look in "mine" first,
        // then "shared". If the image isn't in either list (deleted, or
        // visibility changed since the script was paired), fall back to no
        // selection — the row shows the empty-state placeholder and the
        // user can still Pick or publish without an image.
        if (defaultImageId != null) {
          if (mine.some((i) => i.id === defaultImageId)) {
            setSelectedId(defaultImageId);
            setSelectedSegment("mine");
          } else if (shared.some((i) => i.id === defaultImageId)) {
            setSelectedId(defaultImageId);
            setSelectedSegment("shared");
          }
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setImagesError(
          err instanceof Error ? err.message : "Failed to load images.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [open, defaultImageId]);

  // Resolve the current selection into a `PlaceholderImage` for the row.
  // Returns null when nothing is selected — the row renders its empty state.
  const pairedImage = useMemo<PlaceholderImage | null>(() => {
    if (selectedId == null) return null;
    if (selectedSegment === "mine") {
      const hit = myImages.find((i) => i.id === selectedId);
      return hit ? toPlaceholderImage(hit, "user") : null;
    }
    if (selectedSegment === "shared") {
      const hit = sharedImages.find((i) => i.id === selectedId);
      return hit ? toPlaceholderImage(hit, "library") : null;
    }
    return null;
  }, [selectedId, selectedSegment, myImages, sharedImages]);

  // The selected My Images image — used to drive the auto-share info banner.
  const selectedMyImage = useMemo(() => {
    if (selectedSegment !== "mine" || selectedId == null) return null;
    return myImages.find((i) => i.id === selectedId) ?? null;
  }, [selectedSegment, selectedId, myImages]);

  // A My Images image triggers auto-share only when it isn't already public.
  // Shared images are already global so there's no share-on-publish behavior.
  const willAutoShare =
    selectedMyImage !== null && selectedMyImage.is_global !== true;

  // Picker callback — fires when the user picks an image from
  // LibrarySelectModal opened on top of this dialog. Mirrors the
  // id+segment dance the modal's own state used previously.
  function handlePickerSelect(image: PlaceholderImage) {
    const parsedId = parseInt(image.id, 10);
    if (Number.isNaN(parsedId)) return;
    setSelectedId(parsedId);
    setSelectedSegment(image.source === "user" ? "mine" : "shared");
  }

  function handleClear() {
    setSelectedId(null);
    setSelectedSegment(null);
  }

  async function handlePublish() {
    if (scriptId == null || publishing) return;
    setPublishing(true);
    setPublishError(null);
    try {
      const updated = await userScriptsApi.publish(
        scriptId,
        true,
        selectedId ?? null,
      );
      onSuccess(updated);
    } catch (err) {
      setPublishError(
        err instanceof Error ? err.message : "Failed to publish script.",
      );
    } finally {
      setPublishing(false);
    }
  }

  const headingTitle = scriptTitle
    ? `Publish "${scriptTitle}" to Community`
    : "Publish to Community";

  return (
    <>
      <Dialog
        open={open}
        onClose={publishing ? undefined : onClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.5,
            px: 2,
          }}
        >
          <Typography variant="h3" component="span">
            {headingTitle}
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close"
            disabled={publishing}
          >
            <CloseOutlinedIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Share your script with the community. Pair it with an image to
              make it easier to try.
            </Typography>

            {publishError && (
              <Alert severity="error" onClose={() => setPublishError(null)}>
                {publishError}
              </Alert>
            )}

            {imagesError && (
              <Alert severity="warning" onClose={() => setImagesError(null)}>
                {imagesError}
              </Alert>
            )}

            <PairedImageRow
              image={pairedImage}
              isTemp={false}
              onPick={() => setPickerOpen(true)}
              onClear={handleClear}
            />

            {willAutoShare && (
              <Alert severity="info" variant="outlined">
                This image will be shared with the community when you publish.
              </Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button variant="outlined" onClick={onClose} disabled={publishing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handlePublish}
            disabled={publishing || scriptId == null}
            startIcon={publishing ? <CircularProgress size={16} /> : undefined}
          >
            {publishing ? "Publishing…" : "Publish"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image picker, stacked on top of the publish dialog. MUI's Dialog
          z-index handles the stacking — this dialog stays mounted underneath
          and refreshes its row when the picker fires onSelect. */}
      <LibrarySelectModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handlePickerSelect}
      />
    </>
  );
}
